// v5.45.0 split payments — pure helpers for the "Split payment" / "Edit split" editor (tested in payoutSplit.test.js).
// Money is compared in whole cents so 0.1 + 0.2 never blocks a correct split.

export const COMPANY_SHORT = { harry_dao: 'Harry Dao', solutions: 'Solutions' };

export const toCents = (v) => {
  const s = String(v ?? '').replace(/[$,\s]/g, '');
  if (!/^-?\d*(\.\d{0,2})?$/.test(s) || s === '' || s === '-' || s === '.') return NaN;
  return Math.round(Number(s) * 100);
};

export const fmtCents = (c) => (c / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const blankPart = (amount = '') => ({ key: Math.random().toString(36).slice(2), id: null, paying_company: '', amount, delayed: false, expected_date: '', locked: false });

// The editor's rows from the server's parts (Edit split): locked = already posted in Books.
export const rowsFromParts = (parts) => (parts || []).map((p) => ({
  key: `p${p.id}`, id: p.id, paying_company: p.paying_company || '', amount: Number(p.amount).toFixed(2),
  delayed: p.status === 'delayed', expected_date: p.expected_date || '', locked: !!p.locked,
}));

// What the editor says and whether Mark paid / Save may be pressed: every row has a company and an amount > 0,
// and the rows add up EXACTLY to the batch total — v5.49.0: unless allowDiff (a regular batch: the difference carries
// to the employee's next payout; the reason is checked by the caller).
export function splitState(rows, total, { allowDiff = false } = {}) {
  const totalC = toCents(total);
  let sumC = 0;
  const problems = [];
  (rows || []).forEach((r, i) => {
    const c = toCents(r.amount);
    if (!r.paying_company) problems.push(`Part ${i + 1}: choose the company`);
    if (Number.isNaN(c) || c <= 0) problems.push(`Part ${i + 1}: enter an amount more than $0`);
    else sumC += c;
  });
  if (!rows || rows.length === 0) problems.push('Add at least one part');
  const diffC = totalC - sumC;
  return { sumC, totalC, diffC, problems, ready: problems.length === 0 && (allowDiff || diffC === 0) && rows.length <= 20 };
}

// v5.49.0 carry-over helpers. diffC = owed - paid in cents (overpaid -> negative).
export const fmtSignedCents = (c) => `${c < 0 ? '-' : '+'}${fmtCents(Math.abs(c))}`;

// "Paying $100.00 more than owed — -$100.00 will carry to Tam Tran's next payout" ('' when exact).
export function carryText(diffC, employee) {
  if (!diffC) return '';
  const who = employee ? `${employee}'s` : "the employee's";
  return `Paying ${fmtCents(Math.abs(diffC))} ${diffC < 0 ? 'more' : 'less'} than owed — ${fmtSignedCents(diffC)} will carry to ${who} next payout`;
}

// "Paid $1,100.00 (owed $1,000.00; -$100.00 carried to next period)" for a carry-over view from the API.
export function paidOwedLine(c) {
  if (!c) return '';
  return `Paid ${fmtCents(toCents(c.paid_amount))} (owed ${fmtCents(toCents(c.owed_amount))}; ${fmtSignedCents(toCents(c.amount))} carried to next period)`;
}

// "waiting" / "applied to batch #12" / "partly applied (batch #12) — -$200.00 still waiting" / "cancelled (paid exactly)"
export function carryStatus(c) {
  if (!c) return '';
  const on = [...new Set((c.applied || []).map((a) => `#${a.batch_id}`))].join(', ');
  if (c.status === 'applied') return `applied to batch ${on}`;
  if (c.status === 'partly_applied') return `partly applied (batch ${on}) — ${fmtSignedCents(toCents(c.remaining))} still waiting`;
  if (c.status === 'cancelled') return 'cancelled (paid exactly after Edit split)';
  return 'waiting for the next payout';
}

// "Expected adjustment from your last payout: -$100.00 (overpaid on batch #12, 11/03/2026 — reason)"
export function expectedLine(c) {
  return `Expected adjustment from your last payout: ${fmtSignedCents(toCents(c.remaining))} (${c.direction} on batch #${c.source_batch_id}`
    + `${c.source_paid_date ? `, ${c.source_paid_date}` : ''} — ${c.reason})`;
}

// The API body (amounts as 2-decimal strings; expected date only on a delayed part).
export const partsPayload = (rows, withIds = false) => rows.map((r) => ({
  ...(withIds && r.id ? { id: r.id } : {}),
  paying_company: r.paying_company,
  amount: (toCents(r.amount) / 100).toFixed(2),
  delayed: !!r.delayed,
  expected_date: r.delayed && r.expected_date ? r.expected_date : null,
}));

// "Paid — $1,000.00 still to come" for a paid batch with delayed parts; plain "Paid" otherwise.
export function paidLabel(batch) {
  if (!batch || batch.status !== 'paid') return batch?.status || '';
  const d = Number(batch.delayed_total || 0);
  return d > 0 ? `Paid — ${fmtCents(Math.round(d * 100))} still to come` : 'Paid';
}

// One line per part: "$3,000.00 — Solutions" / "$500.00 — Harry Dao · Pending — expected 12/01/2026".
export function partLine(p) {
  const who = p.short_name || COMPANY_SHORT[p.paying_company] || p.paying_company_name || '—';
  const amt = fmtCents(Math.round(Number(p.amount) * 100));
  if (p.status === 'delayed') {
    const m = String(p.expected_date || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return `${amt} — ${who} · Pending${m ? ` — expected ${m[2]}/${m[3]}/${m[1]}` : ''}`;
  }
  return `${amt} — ${who}`;
}
